/**
 * Frontend Constants for HR Platform
 *
 * Centralized configuration values, route definitions,
 * and constant values used throughout the frontend.
 */

// ============================================
// API Configuration
// ============================================

/**
 * Base URL for the backend API
 * Uses environment variable or defaults to relative API path
 */
export const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL || '/api';

/**
 * Base URL for the BFF (Backend-for-Frontend) API routes
 */
export const BFF_API_URL = '/api';

/**
 * Whether to use BFF mode (routes through Next.js API)
 * Hardcoded to true for production use through proxy
 */
export const USE_BFF = true;

// ============================================
// Application Configuration
// ============================================

/**
 * Application name displayed in UI
 */
export const APP_NAME = 'HR Platform';

/**
 * Application version
 */
export const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0';

/**
 * Default locale for formatting
 */
export const DEFAULT_LOCALE = 'en-US';

/**
 * Default currency
 */
export const DEFAULT_CURRENCY = 'USD';

/**
 * Default timezone
 */
export const DEFAULT_TIMEZONE = 'America/New_York';

// ============================================
// Route Definitions
// ============================================

/**
 * Application routes organized by feature
 */
export const ROUTES = {
  // Auth routes
  auth: {
    login: '/login',
    register: '/register',
    forgotPassword: '/forgot-password',
    resetPassword: '/reset-password',
    verifyEmail: '/verify-email',
    mfa: '/mfa',
    sso: '/sso',
  },

  // Dashboard
  dashboard: '/dashboard',

  // Employee routes
  employees: {
    list: '/employees',
    new: '/employees/new',
    view: (id: string) => `/employees/${id}`,
    edit: (id: string) => `/employees/${id}/edit`,
    documents: (id: string) => `/employees/${id}/documents`,
    timeline: (id: string) => `/employees/${id}/timeline`,
  },

  // Organization routes
  organization: {
    chart: '/org-chart',
    departments: '/departments',
    departmentView: (id: string) => `/departments/${id}`,
    locations: '/locations',
    locationView: (id: string) => `/locations/${id}`,
    jobs: '/jobs',
    jobView: (id: string) => `/jobs/${id}`,
  },

  // Recruiting routes
  recruiting: {
    dashboard: '/recruiting',
    jobs: '/recruiting/jobs',
    jobView: (id: string) => `/recruiting/jobs/${id}`,
    candidates: '/recruiting/candidates',
    candidateView: (id: string) => `/recruiting/candidates/${id}`,
    interviews: '/recruiting/interviews',
    offers: '/recruiting/offers',
    pipeline: '/recruiting/pipeline',
  },

  // Time & Attendance routes
  time: {
    dashboard: '/time',
    timesheet: '/time/timesheet',
    clock: '/time/clock',
    approvals: '/time/approvals',
    reports: '/time/reports',
  },

  // Leave routes
  leave: {
    dashboard: '/leave',
    request: '/leave/request',
    requests: '/leave/requests',
    balances: '/leave/balances',
    calendar: '/leave/calendar',
    approvals: '/leave/approvals',
  },

  // Payroll routes
  payroll: {
    dashboard: '/payroll',
    runs: '/payroll/runs',
    runView: (id: string) => `/payroll/runs/${id}`,
    stubs: '/payroll/stubs',
    stubView: (id: string) => `/payroll/stubs/${id}`,
    settings: '/payroll/settings',
  },

  // Benefits routes
  benefits: {
    dashboard: '/benefits',
    enrollment: '/benefits/enrollment',
    plans: '/benefits/plans',
    planView: (id: string) => `/benefits/plans/${id}`,
    dependents: '/benefits/dependents',
  },

  // Compensation routes
  compensation: {
    dashboard: '/compensation',
    salaryBands: '/compensation/salary-bands',
    reviews: '/compensation/reviews',
    reviewView: (id: string) => `/compensation/reviews/${id}`,
    bonuses: '/compensation/bonuses',
    analytics: '/compensation/analytics',
  },

  // Performance routes
  performance: {
    dashboard: '/performance',
    reviews: '/performance/reviews',
    reviewView: (id: string) => `/performance/reviews/${id}`,
    goals: '/performance/goals',
    goalView: (id: string) => `/performance/goals/${id}`,
    cycles: '/performance/cycles',
    cycleView: (id: string) => `/performance/cycles/${id}`,
    feedback: '/performance/feedback',
  },

  // Learning routes
  learning: {
    dashboard: '/learning',
    courses: '/learning/courses',
    courseView: (id: string) => `/learning/courses/${id}`,
    myLearning: '/learning/my-learning',
    paths: '/learning/paths',
    pathView: (id: string) => `/learning/paths/${id}`,
  },

  // Reports routes
  reports: {
    dashboard: '/reports',
    headcount: '/reports/headcount',
    turnover: '/reports/turnover',
    demographics: '/reports/demographics',
    compensation: '/reports/compensation',
    timeOff: '/reports/time-off',
    hiring: '/reports/hiring',
    custom: '/reports/custom',
  },

  // Documents routes
  documents: {
    list: '/documents',
    view: (id: string) => `/documents/${id}`,
    upload: '/documents/upload',
  },

  // Notifications routes
  notifications: {
    list: '/notifications',
    preferences: '/notifications/preferences',
  },

  // Settings routes
  settings: {
    dashboard: '/settings',
    company: '/settings/company',
    security: '/settings/security',
    users: '/settings/users',
    integrations: '/settings/integrations',
    integrationView: (id: string) => `/settings/integrations/${id}`,
    webhooks: '/settings/webhooks',
    webhookView: (id: string) => `/settings/webhooks/${id}`,
    featureFlags: '/settings/feature-flags',
    sso: '/settings/sso',
    audit: '/settings/audit',
    waitlist: '/settings/waitlist',
  },

  // Profile routes
  profile: {
    view: '/profile',
    edit: '/profile/edit',
    security: '/profile/security',
  },

  // Help & Support
  help: {
    index: '/help',
    docs: '/help/docs',
    support: '/help/support',
  },
} as const;

// ============================================
// Storage Keys
// ============================================

/**
 * LocalStorage keys used by the application
 */
export const STORAGE_KEYS = {
  // Authentication
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',

  // User preferences
  THEME: 'hr-theme',
  LOCALE: 'hr-locale',
  SIDEBAR_COLLAPSED: 'hr-sidebar-collapsed',

  // UI state
  TABLE_PAGE_SIZE: 'hr-table-page-size',
  RECENT_SEARCHES: 'hr-recent-searches',
  DASHBOARD_LAYOUT: 'hr-dashboard-layout',

  // Feature flags (cached)
  FEATURE_FLAGS: 'hr-feature-flags',

  // Last visited
  LAST_TENANT: 'hr-last-tenant',
  LAST_ROUTE: 'hr-last-route',
} as const;

// ============================================
// Pagination
// ============================================

/**
 * Default page size for lists and tables
 */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Available page size options
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

/**
 * Maximum page size allowed
 */
export const MAX_PAGE_SIZE = 100;

// ============================================
// File Upload Configuration
// ============================================

/**
 * File size limits in bytes
 */
export const FILE_SIZE_LIMITS = {
  /** Default maximum file size (10MB) */
  DEFAULT: 10 * 1024 * 1024,

  /** Maximum avatar/image size (5MB) */
  IMAGE: 5 * 1024 * 1024,

  /** Maximum document size (25MB) */
  DOCUMENT: 25 * 1024 * 1024,

  /** Maximum bulk import file size (50MB) */
  IMPORT: 50 * 1024 * 1024,
} as const;

/**
 * Allowed file types for uploads
 */
export const ALLOWED_FILE_TYPES = {
  /** Image file types */
  images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],

  /** Document file types */
  documents: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
  ],

  /** Spreadsheet file types for imports */
  spreadsheets: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ],

  /** All allowed file types */
  all: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
  ],
} as const;

/**
 * File extension mappings for display
 */
export const FILE_EXTENSIONS = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'text/plain': '.txt',
  'text/csv': '.csv',
} as const;

// ============================================
// UI Configuration
// ============================================

/**
 * Toast notification durations in milliseconds
 */
export const TOAST_DURATION = {
  SHORT: 3000,
  MEDIUM: 5000,
  LONG: 8000,
  PERSISTENT: 0, // Won't auto-dismiss
} as const;

/**
 * Debounce delays in milliseconds
 */
export const DEBOUNCE_DELAY = {
  SEARCH: 300,
  FORM_VALIDATION: 500,
  WINDOW_RESIZE: 150,
  AUTO_SAVE: 1000,
} as const;

/**
 * Animation durations in milliseconds
 */
export const ANIMATION_DURATION = {
  FAST: 150,
  NORMAL: 200,
  SLOW: 300,
} as const;

// ============================================
// API Configuration
// ============================================

/**
 * Request timeout in milliseconds
 */
export const REQUEST_TIMEOUT = 30000;

/**
 * Maximum retry attempts for failed requests
 */
export const MAX_RETRY_ATTEMPTS = 3;

/**
 * Retry delay in milliseconds (used with exponential backoff)
 */
export const RETRY_DELAY = 1000;

// ============================================
// Date & Time Configuration
// ============================================

/**
 * Date format strings
 */
export const DATE_FORMATS = {
  /** Display format: Jan 15, 2024 */
  DISPLAY: 'MMM d, yyyy',

  /** Full display format: January 15, 2024 */
  DISPLAY_LONG: 'MMMM d, yyyy',

  /** Short format: 1/15/24 */
  SHORT: 'M/d/yy',

  /** ISO format for API: 2024-01-15 */
  ISO: 'yyyy-MM-dd',

  /** Date with time: Jan 15, 2024 3:30 PM */
  DATETIME: 'MMM d, yyyy h:mm a',

  /** Time only: 3:30 PM */
  TIME: 'h:mm a',

  /** Month and year: January 2024 */
  MONTH_YEAR: 'MMMM yyyy',
} as const;

// ============================================
// Validation Constants
// ============================================

/**
 * Validation constraints
 */
export const VALIDATION = {
  /** Minimum password length */
  PASSWORD_MIN_LENGTH: 8,

  /** Maximum password length */
  PASSWORD_MAX_LENGTH: 128,

  /** Maximum name length */
  NAME_MAX_LENGTH: 100,

  /** Maximum email length */
  EMAIL_MAX_LENGTH: 255,

  /** Maximum description/notes length */
  DESCRIPTION_MAX_LENGTH: 2000,

  /** Maximum comment length */
  COMMENT_MAX_LENGTH: 500,

  /** Employee number max length */
  EMPLOYEE_NUMBER_MAX_LENGTH: 20,
} as const;

// ============================================
// Employment Types & Statuses
// ============================================

/**
 * Employment status options
 */
export const EMPLOYMENT_STATUS = {
  ACTIVE: 'ACTIVE',
  ON_LEAVE: 'ON_LEAVE',
  TERMINATED: 'TERMINATED',
  SUSPENDED: 'SUSPENDED',
} as const;

/**
 * Employment type options
 */
export const EMPLOYMENT_TYPE = {
  FULL_TIME: 'FULL_TIME',
  PART_TIME: 'PART_TIME',
  CONTRACT: 'CONTRACT',
  INTERN: 'INTERN',
  TEMPORARY: 'TEMPORARY',
} as const;

/**
 * Human-readable labels for employment status
 */
export const EMPLOYMENT_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  ON_LEAVE: 'On Leave',
  TERMINATED: 'Terminated',
  SUSPENDED: 'Suspended',
};

/**
 * Human-readable labels for employment type
 */
export const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: 'Full Time',
  PART_TIME: 'Part Time',
  CONTRACT: 'Contract',
  INTERN: 'Intern',
  TEMPORARY: 'Temporary',
};

// ============================================
// Leave Types
// ============================================

/**
 * Leave type options
 */
export const LEAVE_TYPE = {
  VACATION: 'VACATION',
  SICK: 'SICK',
  PERSONAL: 'PERSONAL',
  BEREAVEMENT: 'BEREAVEMENT',
  JURY_DUTY: 'JURY_DUTY',
  PARENTAL: 'PARENTAL',
  UNPAID: 'UNPAID',
  OTHER: 'OTHER',
} as const;

/**
 * Human-readable labels for leave types
 */
export const LEAVE_TYPE_LABELS: Record<string, string> = {
  VACATION: 'Vacation',
  SICK: 'Sick Leave',
  PERSONAL: 'Personal',
  BEREAVEMENT: 'Bereavement',
  JURY_DUTY: 'Jury Duty',
  PARENTAL: 'Parental Leave',
  UNPAID: 'Unpaid Leave',
  OTHER: 'Other',
};

// ============================================
// Approval Statuses
// ============================================

/**
 * Common approval status values
 */
export const APPROVAL_STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
} as const;

/**
 * Human-readable labels for approval statuses
 */
export const APPROVAL_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

// ============================================
// Color Schemes for Status
// ============================================

/**
 * Status color mappings for UI components
 */
export const STATUS_COLORS = {
  // Success states
  ACTIVE: 'green',
  APPROVED: 'green',
  COMPLETED: 'green',

  // Warning states
  PENDING: 'yellow',
  ON_LEAVE: 'yellow',
  IN_PROGRESS: 'yellow',

  // Error states
  REJECTED: 'red',
  TERMINATED: 'red',
  FAILED: 'red',

  // Neutral states
  CANCELLED: 'gray',
  DRAFT: 'gray',
  INACTIVE: 'gray',
} as const;
